'use client';

/**
 * USER DOCS: changes to this component should be accompanied by a
 * corresponding update to the user guide:
 * next_portal/user-docs/content/docs/total-connect/email-security.mdx
 * (published at user-docs.totalaccess.co.za/total-connect/email-security)
 */

/**
 * EmailSecurityBanner Component
 * 
 * Displays security warnings for emails based on spam/phishing analysis.
 * Now includes Deep Security Analysis integration for advanced threat detection.
 * 
 * Shows appropriate banners for:
 * - Impersonation attempts (name matches contact but email doesn't)
 * - High spam scores
 * - Unknown external senders
 * - First-time senders
 * - Failed authentication (SPF/DKIM/DMARC)
 * - Deep analysis threat indicators (BEC, typosquatting, etc.)
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { 
  AlertTriangle, 
  ShieldAlert, 
  ShieldQuestion,
  UserX,
  Mail,
  MailWarning,
  ShieldX,
  ShieldCheck,
  ChevronDown,
  ChevronUp,
  UserPlus,
  Check,
  Flag,
  Ban,
  X,
  Scan,
  Globe,
  Server,
  Link,
  FileWarning,
  TrendingUp,
  Loader2,
  RefreshCw,
  Sparkles,
  MessageSquare
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { authService } from '@/lib/authService';
import { notify } from '@/lib/notifications';
import type { EmailSecuritySettings } from '@/hooks/useEmailSecuritySettings';
import { useEmailStore } from '../stores/emailStore';

// Security analysis structure from backend emailSecurityService.js
export interface SecurityAnalysis {
  // Spam analysis
  spam_score: number;
  spam_status: string;
  spam_action: string;
  is_spam: boolean;
  
  // Authentication
  dkim_passed: boolean | null;
  spf_passed: boolean | null;
  dmarc_passed: boolean | null;
  
  // TotalAccess custom checks
  is_external: boolean;
  is_known_contact: boolean;
  is_first_time_sender: boolean;
  is_potential_impersonation: boolean;
  impersonation_details?: {
    display_name: string;
    claimed_email: string;
    real_contact: {
      type: string;
      name: string;
      email: string;
      company_name?: string;
      expected_domains?: string[];
    };
  } | null;
  
  // Contact match info
  matched_contact?: {
    type: string;
    id: string | number;
    name: string;
    email: string;
  } | null;
  
  // Aggregated warnings
  warnings: string[];
  warning_level: 'none' | 'low' | 'medium' | 'high' | 'critical';
  
  // Metadata
  analyzed_at?: string;
  /** Sender matched client email_safe_senders during intake */
  safe_sender_whitelisted?: boolean;
  safe_sender_trust_level?: string | null;
  /** Mirror of deep-scan results on the message (when available without loading full deep analysis) */
  deep_threat_score?: number;
  deep_threat_category?: string;
}

// Deep Security Analysis structure from emailDeepSecurityService.js
export interface DeepSecurityAnalysis {
  // Threat verdict
  threat_score: number;
  threat_confidence: number;
  threat_category: 'clean' | 'suspicious' | 'uncertain' | 'spam' | 'phishing' | 'bec' | 'malware' | 'spoofing';
  threat_indicators: Array<{
    indicator: string;
    severity: 'low' | 'medium' | 'high' | 'critical';
    explanation: string;
  }>;
  
  // IP Reputation
  sender_ip?: string;
  ip_reputation_score?: number;
  ip_country_code?: string;
  ip_is_datacenter?: boolean;
  ip_blacklist_hits?: Array<{ source: string; reason?: string }>;
  
  // Domain Reputation
  sender_domain?: string;
  domain_age_days?: number;
  domain_reputation_score?: number;
  domain_has_valid_mx?: boolean;
  domain_has_dmarc?: boolean;
  domain_has_valid_spf?: boolean;
  domain_is_disposable?: boolean;
  domain_is_free_email?: boolean;
  domain_is_trusted_free_provider?: boolean;
  domain_free_provider?: string | null;
  domain_similar_to_known?: {
    matched: string;
    similarity: number;
    type: string;
  } | string | null;
  
  // Header Analysis
  sender_reply_to_mismatch?: boolean;
  sender_return_path_mismatch?: boolean;
  header_anomalies?: Array<{
    type: string;
    severity: string;
    description: string;
  }>;
  
  // Content Analysis (BEC)
  content_urgency_score?: number;
  content_financial_indicators?: string[];
  content_suspicious_urls?: Array<{
    url: string;
    risk: string;
    reason?: string;
  }>;
  content_link_mismatch_detected?: boolean;
  
  // Sender Display Name
  sender_display_name_analysis?: {
    has_unicode_tricks?: boolean;
    has_email_in_name?: boolean;
    lookalike_detected?: boolean;
  };
  
  // Sender History
  sender_history?: {
    total_emails: number;
    first_seen?: string;
    last_seen?: string;
    threads_count?: number;
    reply_rate?: number;
  } | string;

  /** Deep scan skipped spoofing/brand rules for email_safe_senders match */
  safe_sender_structural_bypass?: boolean;
  safe_sender_trust_level?: string | null;
  
  // Recommendations
  recommended_action: 'deliver' | 'flag' | 'quarantine' | 'delete';
  override_initial_decision?: boolean;
  
  // AI Analysis (Gemini)
  ai_analyzed?: boolean;
  ai_threat_assessment?: string | null;
  ai_confidence?: number | null;
  ai_analysis_cost?: number | null;
  
  // Metadata
  analyzed_at: string;
  analysis_duration_ms?: number;

  /** RDAP-enriched registration metadata (from emailDeepSecurityService) */
  domain_history?: {
    registration_date?: string;
    source?: string;
  } | string | null;
}

// Thread-level sender identification (cached)
export interface SenderIdentification {
  type: string;
  id: string | number | null;
  name: string;
  email: string;
  verified: boolean;
  verified_by?: number | null;
  verified_by_name?: string | null;
  verified_at?: string | Date | null;
  linked_at?: string | Date | null;
}

export interface EmailSecurityBannerProps {
  securityAnalysis: SecurityAnalysis | null;
  senderEmail?: string;
  senderName?: string;
  className?: string;
  // Thread-level verification state (passed from parent)
  threadId?: string;
  messageId?: string;  // For deep analysis
  accountId?: number;
  senderIdentification?: SenderIdentification | null;
  securityWarningsDismissed?: boolean;
  // Deep analysis
  deepAnalysisAvailable?: boolean;
  deepAnalysis?: DeepSecurityAnalysis | null;
  onDeepAnalysisLoaded?: (analysis: DeepSecurityAnalysis) => void;
  // Callbacks
  onAddToContacts?: (email: string, name: string) => void;
  onMarkAsSafe?: () => void;
  onReportPhishing?: () => void;
  onBlockSender?: () => void;
  onVerifySender?: (action: 'verify' | 'dismiss' | 'link', contactId?: number, contactType?: string) => void;
  onSenderVerified?: (verified: boolean, details?: { movedToFolder?: string | null }) => void; // Callback when verification state changes
  compact?: boolean; // For mobile/smaller displays
  // Client-level email security settings (from client_config)
  settings?: EmailSecuritySettings | null;
}

// Banner configurations by type
const BANNER_CONFIGS = {
  // Deep analysis threat categories (highest priority)
  phishing_threat: {
    icon: ShieldAlert,
    bgClass: 'bg-red-100 dark:bg-red-900/30 border-red-300 dark:border-red-700',
    textClass: 'text-red-900 dark:text-red-100',
    iconClass: 'text-red-700 dark:text-red-300',
    title: 'Total Threat Analysis - Phishing Threat',
    priority: 0
  },
  bec_threat: {
    icon: AlertTriangle,
    bgClass: 'bg-red-100 dark:bg-red-900/30 border-red-300 dark:border-red-700',
    textClass: 'text-red-900 dark:text-red-100',
    iconClass: 'text-red-700 dark:text-red-300',
    title: 'Total Threat Analysis - BEC Risk',
    priority: 0
  },
  spoofing_threat: {
    icon: UserX,
    bgClass: 'bg-red-100 dark:bg-red-900/30 border-red-300 dark:border-red-700',
    textClass: 'text-red-900 dark:text-red-100',
    iconClass: 'text-red-700 dark:text-red-300',
    title: 'Total Threat Analysis - Spoofing',
    priority: 0
  },
  suspicious_sender: {
    icon: ShieldAlert,
    bgClass: 'bg-orange-100 dark:bg-orange-900/30 border-orange-300 dark:border-orange-700',
    textClass: 'text-orange-900 dark:text-orange-100',
    iconClass: 'text-orange-700 dark:text-orange-300',
    title: 'Total Threat Analysis - Suspicious Sender',
    priority: 0
  },
  impersonation: {
    icon: ShieldAlert,
    secondaryIcon: UserX,
    bgClass: 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800',
    textClass: 'text-red-800 dark:text-red-200',
    iconClass: 'text-red-600 dark:text-red-400',
    title: 'Total Threat Analysis - Potential Impersonation Attempt',
    priority: 1
  },
  spam: {
    icon: ShieldAlert,
    bgClass: 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800',
    textClass: 'text-red-800 dark:text-red-200',
    iconClass: 'text-red-600 dark:text-red-400',
    title: 'Spam Detected',
    priority: 2
  },
  spam_warning: {
    icon: ShieldAlert,
    bgClass: 'bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800',
    textClass: 'text-orange-800 dark:text-orange-200',
    iconClass: 'text-orange-600 dark:text-orange-400',
    title: 'Possible Spam',
    priority: 3
  },
  auth_failure: {
    icon: ShieldX,
    bgClass: 'bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800',
    textClass: 'text-orange-800 dark:text-orange-200',
    iconClass: 'text-orange-600 dark:text-orange-400',
    title: 'Email Authentication Failed',
    priority: 4
  },
  unknown_first_time: {
    icon: ShieldQuestion,
    bgClass: 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800',
    textClass: 'text-amber-800 dark:text-amber-200',
    iconClass: 'text-amber-600 dark:text-amber-400',
    title: 'External Sender - Not in Contacts',
    priority: 5
  },
  unknown_sender: {
    icon: ShieldQuestion,
    bgClass: 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800',
    textClass: 'text-yellow-800 dark:text-yellow-200',
    iconClass: 'text-yellow-600 dark:text-yellow-400',
    title: 'External Sender - Not in Contacts',
    priority: 6
  },
  first_time: {
    icon: MailWarning,
    bgClass: 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800',
    textClass: 'text-blue-800 dark:text-blue-200',
    iconClass: 'text-blue-600 dark:text-blue-400',
    title: 'First Email from This Sender',
    priority: 7
  },
  // Informational banner for external senders after deep analysis clears threats
  external_info: {
    icon: Globe,
    bgClass: 'bg-slate-50 dark:bg-slate-900/20 border-slate-200 dark:border-slate-700',
    textClass: 'text-slate-700 dark:text-slate-300',
    iconClass: 'text-slate-500 dark:text-slate-400',
    title: 'External Sender',
    priority: 8
  }
};

type BannerType = keyof typeof BANNER_CONFIGS;

/** Banners that use a strong red outline + calmer dark-mode expanded body */
const DEEP_THREAT_BANNER_TYPES = new Set<BannerType>([
  'phishing_threat',
  'bec_threat',
  'spoofing_threat',
  'suspicious_sender',
]);

// Special marker to indicate deep analysis cleared the email
const DEEP_ANALYSIS_CLEARED = 'CLEARED' as const;

/**
 * When DNS alignment and authentication agree, "spoofing" from AI/heuristics is often a false positive
 * (e.g. transactional mail from banks with noreply display names).
 */
function shouldDowngradeDeepSpoofingBanner(
  deep: DeepSecurityAnalysis,
  basic: SecurityAnalysis | null | undefined,
): boolean {
  if (deep.threat_category !== 'spoofing') return false;
  if (deep.sender_reply_to_mismatch || deep.sender_return_path_mismatch) return false;
  if (deep.domain_similar_to_known) return false;
  const ipBad = Array.isArray(deep.ip_blacklist_hits) && deep.ip_blacklist_hits.length > 0;
  if (ipBad) return false;

  const dnsAligned =
    deep.domain_has_valid_spf === true &&
    deep.domain_has_dmarc === true &&
    deep.domain_has_valid_mx !== false;

  const headerAuthOk =
    !!basic &&
    (basic.spf_passed === true || basic.dkim_passed === true || basic.dmarc_passed === true);

  if (dnsAligned && headerAuthOk) return true;

  if (
    dnsAligned &&
    !!basic &&
    (basic.spf_passed === true || basic.dmarc_passed === true) &&
    (deep.threat_score ?? 0) < 60
  ) {
    return true;
  }

  return false;
}

/** Thread list: hide spoofing chip when auth/DNS strongly disagree with the spoof verdict */
export function shouldSuppressListSecurityIndicator(analysis: SecurityAnalysis | null | undefined): boolean {
  if (!analysis) return false;
  const cat = analysis.deep_threat_category;
  if (cat !== 'spoofing') return false;
  const score = analysis.deep_threat_score;
  const authOk =
    analysis.spf_passed === true || analysis.dkim_passed === true || analysis.dmarc_passed === true;
  if (authOk && (score == null || score < 55)) return true;
  return false;
}

// Get banner type from deep analysis (highest priority)
// Returns BannerType for threats, 'CLEARED' if email is confirmed safe, or null if no deep analysis
function getBannerTypeFromDeepAnalysis(
  deepAnalysis: DeepSecurityAnalysis | null,
  basicAnalysis?: SecurityAnalysis | null,
): BannerType | typeof DEEP_ANALYSIS_CLEARED | null {
  if (!deepAnalysis) return null;
  
  // Don't flag trusted free email providers (Gmail, Outlook.com, etc.) as suspicious
  // unless they have actual threat indicators like phishing or BEC
  const isTrustedProvider = deepAnalysis.domain_is_trusted_free_provider;
  
  // Map threat_category to banner type
  switch (deepAnalysis.threat_category) {
    case 'phishing':
      return 'phishing_threat';
    case 'bec':
      return 'bec_threat';
    case 'spoofing':
      // Don't show spoofing for trusted free providers
      if (isTrustedProvider) return DEEP_ANALYSIS_CLEARED;
      if (shouldDowngradeDeepSpoofingBanner(deepAnalysis, basicAnalysis)) return DEEP_ANALYSIS_CLEARED;
      return 'spoofing_threat';
    case 'suspicious':
      // Only show suspicious banner if threat score is significant AND not from trusted provider
      if (isTrustedProvider) return DEEP_ANALYSIS_CLEARED;
      if (deepAnalysis.threat_score >= 30) return 'suspicious_sender';
      return DEEP_ANALYSIS_CLEARED; // Low score = cleared
    case 'clean':
    case 'uncertain':
      // Deep analysis confirmed email is clean or low risk - hide banner
      if (deepAnalysis.threat_score < 30) return DEEP_ANALYSIS_CLEARED;
      return null; // Uncertain with higher score - fall back to basic analysis
    default:
      // For high threat scores without specific category
      // But not for trusted providers unless score is very high
      if (isTrustedProvider && deepAnalysis.threat_score < 70) return DEEP_ANALYSIS_CLEARED;
      if (deepAnalysis.threat_score >= 60) return 'suspicious_sender';
      // Low threat score = cleared
      if (deepAnalysis.threat_score < 30) return DEEP_ANALYSIS_CLEARED;
      return null;
  }
}

// Determine which banner type to show based on security analysis
function getBannerType(analysis: SecurityAnalysis): BannerType | null {
  // Critical: Impersonation
  if (analysis.is_potential_impersonation) return 'impersonation';
  
  // Critical/High: Spam
  if (analysis.is_spam || analysis.spam_score >= 10) return 'spam';
  if (analysis.spam_score >= 6) return 'spam_warning';
  
  // Check auth failures for external emails
  if (analysis.is_external && (
    analysis.dkim_passed === false || 
    analysis.spf_passed === false || 
    analysis.dmarc_passed === false
  )) {
    return 'auth_failure';
  }
  
  // High: Unknown external + first time
  if (analysis.is_external && !analysis.is_known_contact && analysis.is_first_time_sender) {
    return 'unknown_first_time';
  }
  
  // Medium: Unknown external (not first time)
  if (analysis.is_external && !analysis.is_known_contact) return 'unknown_sender';
  
  // Low: First time sender (but known contact)
  if (analysis.is_first_time_sender && analysis.is_known_contact) return 'first_time';
  
  return null;
}

// Get description for the banner
function getBannerDescription(type: BannerType, analysis: SecurityAnalysis, senderEmail?: string, deepAnalysis?: DeepSecurityAnalysis | null): string {
  switch (type) {
    // Deep analysis threat categories (highest priority)
    case 'phishing_threat':
      return `Total Threat Analysis has detected phishing indicators in this email. Do NOT click any links or download attachments. The sender may be attempting to steal your credentials or personal information.`;
    
    case 'bec_threat':
      return `This email shows patterns consistent with Business Email Compromise (BEC) attacks. These often involve urgent requests for money transfers or sensitive information. Verify any requests through a separate channel before taking action.`;
    
    case 'spoofing_threat': {
      // Check if auth records actually exist (domain has SPF/DMARC even if header parsing returned null)
      const hasAuthRecords = deepAnalysis?.domain_has_valid_spf || deepAnalysis?.domain_has_dmarc;
      const authPassed = analysis.spf_passed === true || analysis.dkim_passed === true || analysis.dmarc_passed === true;
      // Build explanation based on actual threat indicators
      const reasons: string[] = [];
      if (deepAnalysis?.domain_similar_to_known) {
        const similar = typeof deepAnalysis.domain_similar_to_known === 'string' 
          ? deepAnalysis.domain_similar_to_known 
          : deepAnalysis.domain_similar_to_known.matched;
        reasons.push(`the domain appears similar to "${similar}" (possible typosquatting)`);
      }
      if (deepAnalysis?.domain_age_days != null && deepAnalysis.domain_age_days < 30) {
        reasons.push('the domain is very new');
      }
      if (deepAnalysis?.domain_reputation_score != null && deepAnalysis.domain_reputation_score < -20) {
        reasons.push('the domain has poor reputation');
      }
      if (analysis.is_potential_impersonation) {
        reasons.push('the sender may be impersonating a known contact');
      }
      if (!hasAuthRecords && !authPassed) {
        reasons.push('the sender domain lacks proper authentication records (SPF/DKIM/DMARC)');
      }
      if (reasons.length > 0) {
        return `Total Threat Analysis has flagged this email because ${reasons.join(', ')}. Exercise caution with any requests, links, or attachments.`;
      }
      if (hasAuthRecords || authPassed) {
        return `Total Threat Analysis has flagged this email as potentially spoofed based on domain analysis and threat indicators. Although email authentication records exist, other risk signals were detected. Exercise caution.`;
      }
      return `This email appears to be spoofed - the sender's identity cannot be verified and may be forged. The sender domain lacks proper authentication records (SPF/DKIM/DMARC) that legitimate businesses use.`;
    }
    
    case 'suspicious_sender': {
      const suspReasons: string[] = [];
      if (deepAnalysis?.domain_reputation_score != null && deepAnalysis.domain_reputation_score < -20) {
        suspReasons.push('poor domain reputation');
      }
      if (deepAnalysis?.domain_similar_to_known) {
        suspReasons.push('domain name resembles a known domain');
      }
      if (!deepAnalysis?.domain_has_valid_spf && !deepAnalysis?.domain_has_dmarc && 
          analysis.spf_passed !== true && analysis.dkim_passed !== true) {
        suspReasons.push('missing email authentication records');
      }
      if (analysis.is_first_time_sender) {
        suspReasons.push('first contact from this sender');
      }
      if (suspReasons.length > 0) {
        return `Total Threat Analysis has flagged this email as suspicious: ${suspReasons.join(', ')}. Exercise caution with any requests or links.`;
      }
      return `Total Threat Analysis has flagged this email as suspicious. The sender's domain has risk indicators that warrant caution. Exercise caution with any requests or links.`;
    }
    
    case 'impersonation':
      if (analysis.impersonation_details) {
        const contactRef = analysis.impersonation_details.real_contact;
        // Brand impersonation (e.g., "The Courier Guy" from nifty.ne.jp)
        if (contactRef.type === 'brand') {
          const expectedDomains = contactRef.expected_domains?.join(' or ') || 'their official domain';
          return `The sender claims to be "${analysis.impersonation_details.display_name}" but the email comes from an unrelated domain (${analysis.impersonation_details.claimed_email}). Legitimate emails from ${contactRef.name} would come from ${expectedDomains}. Do NOT click any links or download attachments.`;
        }
        // Contact impersonation
        const contactDesc = contactRef.company_name 
          ? `${contactRef.name} from ${contactRef.company_name}`
          : contactRef.name;
        return `The sender name "${analysis.impersonation_details.display_name}" matches a known contact (${contactDesc}), but the email address (${analysis.impersonation_details.claimed_email}) doesn't match their known email (${contactRef.email}). This could be a phishing attempt.`;
      }
      return `This sender may be impersonating someone you know. Be cautious with any requests or links in this email.`;
    
    case 'spam':
      return `This email has been flagged as spam with a score of ${analysis.spam_score?.toFixed(1) || 'high'}. Exercise extreme caution with any links or attachments.`;
    
    case 'spam_warning':
      return `This email has a spam score of ${analysis.spam_score?.toFixed(1)}. It may be unwanted mail.`;
    
    case 'auth_failure':
      const failures: string[] = [];
      if (analysis.spf_passed === false) failures.push('SPF');
      if (analysis.dkim_passed === false) failures.push('DKIM');
      if (analysis.dmarc_passed === false) failures.push('DMARC');
      return `This email failed ${failures.join(', ')} authentication. The sender's identity could not be verified.`;
    
    case 'unknown_first_time':
      return `This is the first email you've received from this sender, and they are not in your address book. Be cautious with links and attachments.`;
    
    case 'unknown_sender':
      return `This email is from someone outside your organization who isn't in your address book.`;
    
    case 'first_time':
      return `This is the first email you've received from this address, though they are in your contacts.`;
    
    case 'external_info':
      return `This email is from an external sender. Security analysis found no threats.`;
    
    default:
      return '';
  }
}

// Helper to format warning messages
function formatWarning(warning: string): string {
  const warningMessages: Record<string, string> = {
    'external_sender': 'External sender',
    'unknown_sender': 'Sender not in contacts',
    'first_time_sender': 'First time receiving from this sender',
    'potential_impersonation': 'Possible impersonation',
    'dkim_failed': 'DKIM authentication failed',
    'spf_failed': 'SPF authentication failed',
    'dmarc_failed': 'DMARC authentication failed'
  };
  return warningMessages[warning] || warning.replace(/_/g, ' ');
}

export default function EmailSecurityBanner({
  securityAnalysis,
  senderEmail,
  senderName,
  className,
  threadId,
  messageId,
  accountId,
  senderIdentification,
  securityWarningsDismissed,
  deepAnalysisAvailable,
  deepAnalysis: initialDeepAnalysis,
  onDeepAnalysisLoaded,
  onAddToContacts,
  onMarkAsSafe,
  onReportPhishing,
  onBlockSender,
  onVerifySender,
  onSenderVerified,
  compact = false,
  settings
}: EmailSecurityBannerProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isAddingContact, setIsAddingContact] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isDismissed, setIsDismissed] = useState(securityWarningsDismissed || false);
  const [deepAnalysis, setDeepAnalysis] = useState<DeepSecurityAnalysis | null>(initialDeepAnalysis || null);
  const [isLoadingDeepAnalysis, setIsLoadingDeepAnalysis] = useState(false);
  const [isRunningDeepScan, setIsRunningDeepScan] = useState(false);
  const [showDeepAnalysisPanel, setShowDeepAnalysisPanel] = useState(false);
  const [isReanalyzing, setIsReanalyzing] = useState(false);
  const [clearedByDeepAnalysis, setClearedByDeepAnalysis] = useState(false);
  const [resubmitHandled, setResubmitHandled] = useState(false);
  const [disputeComment, setDisputeComment] = useState('');
  const [disputeSubmitting, setDisputeSubmitting] = useState(false);
  const [disputeSubmitted, setDisputeSubmitted] = useState(false);
  
  // Track if we've already attempted to fetch deep analysis (prevents loops)
  const fetchAttemptedRef = useRef(false);
  
  // Deep analysis cache from store - prevents re-fetching when switching between emails
  // Use getState() for cache methods to avoid SSR/hydration issues
  const cacheDeepAnalysis = useEmailStore((state) => state.cacheDeepAnalysis);

  const fetchDeepAnalysis = useCallback(async () => {
    if (!messageId || !accountId) return;

    setIsLoadingDeepAnalysis(true);
    try {
      const tokens = authService.getTokens();
      if (!tokens?.access_token) return;

      const response = await fetch(
        `/api/v1/email/deep-security/${encodeURIComponent(messageId)}?accountId=${accountId}`,
        {
          headers: { 'Authorization': `Bearer ${tokens.access_token}` }
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.analysis) {
          setDeepAnalysis(data.analysis);
          if (messageId) {
            cacheDeepAnalysis(messageId, data.analysis);
          }
          onDeepAnalysisLoaded?.(data.analysis);
        }
      }
    } catch (err) {
      console.error('Failed to fetch deep analysis:', err);
    } finally {
      setIsLoadingDeepAnalysis(false);
    }
  }, [messageId, accountId, onDeepAnalysisLoaded, cacheDeepAnalysis]);
  
  // Reset state when messageId changes (switching to a different email)
  // Check cache first before showing loading state
  useEffect(() => {
    fetchAttemptedRef.current = false;
    setIsExpanded(false);
    setShowDeepAnalysisPanel(false);
    setClearedByDeepAnalysis(false);
    setResubmitHandled(false);
    setDisputeComment('');
    setDisputeSubmitted(false);
    setIsDismissed(securityWarningsDismissed || false);
    
    // Check cache for previously fetched deep analysis
    // Use getState() directly to avoid stale closures and SSR issues
    if (messageId) {
      const cachedAnalysis = useEmailStore.getState().getCachedDeepAnalysis(messageId);
      if (cachedAnalysis) {
        setDeepAnalysis(cachedAnalysis);
        fetchAttemptedRef.current = true; // Don't fetch again since we have cached data
        return;
      }
    }
    
    // No cached data - reset to initial or null
    setDeepAnalysis(initialDeepAnalysis || null);
  }, [messageId, initialDeepAnalysis, securityWarningsDismissed]);
  
  // Invalidate stale cached deepAnalysis when securityAnalysis shows different threat data
  // This happens when ThreadViewer re-scans and refreshes the thread — the message's
  // security_analysis gets updated (deep_threat_score, deep_threat_category) but the
  // banner's deepAnalysis state still holds the old cached result.
  const invalidationFetchRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (!securityAnalysis || !deepAnalysis) return;
    const msgScore = securityAnalysis.deep_threat_score;
    if (msgScore !== undefined && msgScore !== null && msgScore !== deepAnalysis.threat_score) {
      // Message-level data disagrees with cached deep analysis — force re-fetch
      fetchAttemptedRef.current = false;
      setDeepAnalysis(null);
      setClearedByDeepAnalysis(false);
      // Schedule a re-fetch to get the updated deep analysis from the server
      // (auto-fetch effects won't re-trigger since their deps haven't changed)
      if (invalidationFetchRef.current) clearTimeout(invalidationFetchRef.current);
      invalidationFetchRef.current = setTimeout(() => {
        fetchAttemptedRef.current = true;
        fetchDeepAnalysis();
      }, 300);
    }
    return () => {
      if (invalidationFetchRef.current) clearTimeout(invalidationFetchRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [securityAnalysis?.deep_threat_score, securityAnalysis?.deep_threat_category]);
  
  // Auto-fetch deep analysis when expanded and available (only if not already fetched)
  useEffect(() => {
    if (isExpanded && deepAnalysisAvailable && !deepAnalysis && !fetchAttemptedRef.current) {
      fetchAttemptedRef.current = true;
      fetchDeepAnalysis();
    }
  }, [isExpanded, deepAnalysisAvailable, deepAnalysis, fetchDeepAnalysis]);
  
  // NOTE: Auto-fetch on mount was removed. Deep analysis results are only fetched when:
  //   1. The user expands the banner (see effect above)
  //   2. An invalidation is triggered because the message's deep_threat_score changed
  //      (i.e. a new scan was completed server-side and the thread was refreshed)
  // This prevents a GET request to the deep-security endpoint on every email open.
  
  // Run deep security scan
  const runDeepScan = async () => {
    if (!threadId || !messageId || !accountId || isRunningDeepScan) return;
    
    setIsRunningDeepScan(true);
    try {
      const tokens = authService.getTokens();
      if (!tokens?.access_token) {
        notify.error('Please log in');
        return;
      }
      
      const response = await fetch('/api/v1/email/deep-security/scan', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${tokens.access_token}`
        },
        body: JSON.stringify({
          accountId,
          threadId,
          messageId,
          immediate: true, // Get results immediately
          warningLevel: securityAnalysis?.warning_level
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.analysis && data.analysis.status !== 'queued') {
          setDeepAnalysis(data.analysis);
          // Cache the scan result
          if (messageId) {
            cacheDeepAnalysis(messageId, data.analysis);
          }
          onDeepAnalysisLoaded?.(data.analysis);

          // Show toast based on actual threat level
          const score = data.analysis.threat_score || 0;
          const category = data.analysis.threat_category || 'clean';
          if (score >= 60 || ['phishing', 'bec', 'spoofing'].includes(category)) {
            notify.error(`Security threat detected: ${category} (score: ${score}/100)`);
          } else if (score >= 30 || category === 'suspicious') {
            notify.warning(`Suspicious indicators found (score: ${score}/100)`);
          } else {
            notify.success('Deep security scan complete - no threats detected');
          }
          setShowDeepAnalysisPanel(true);
        } else if (data.success && data.analysis && data.analysis.status === 'queued') {
          // The API queues an async River job and returns immediately.
          // Don't cache the pending result or show a success toast —
          // the actual results arrive later once the River worker
          // processes the message and the thread is refreshed.
          notify.info('Security analysis queued — results will appear shortly');
        } else {
          notify.info('Scan queued for background processing');
        }
      } else {
        notify.error('Failed to run deep scan');
      }
    } catch (err) {
      console.error('Deep scan error:', err);
      notify.error('Scan failed');
    } finally {
      setIsRunningDeepScan(false);
    }
  };
  
  // Resubmit email for security reanalysis
  const reanalyzeEmail = async () => {
    if (!threadId || !messageId || !accountId || isReanalyzing) return;
    
    setIsReanalyzing(true);
    try {
      const tokens = authService.getTokens();
      if (!tokens?.access_token) {
        notify.error('Please log in');
        return;
      }
      
      const response = await fetch('/api/v1/email/deep-security/reanalyze', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${tokens.access_token}`
        },
        body: JSON.stringify({
          accountId,
          threadId,
          messageId,
          forceRefresh: true
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setResubmitHandled(true);
        if (data.success && data.analysis && data.analysis.status !== 'queued') {
          setDeepAnalysis(data.analysis);
          // Cache the reanalysis result
          if (messageId) {
            cacheDeepAnalysis(messageId, data.analysis);
          }
          onDeepAnalysisLoaded?.(data.analysis);

          // Check if the new analysis clears the email
          const newBannerType = getBannerTypeFromDeepAnalysis(data.analysis, securityAnalysis);
          if (newBannerType === DEEP_ANALYSIS_CLEARED) {
            setClearedByDeepAnalysis(true);
            notify.success('Email confirmed safe by security analysis');
          } else if (newBannerType) {
            notify.warning('Security concerns still detected');
          } else {
            notify.info('Analysis complete');
          }
        } else if (data.success && data.analysis && data.analysis.status === 'queued') {
          // The API queues an async River job and returns immediately.
          // Don't cache the pending result or show a success toast.
          notify.info('Reanalysis queued - results will update shortly');
          // Poll for results after a delay
          setTimeout(() => {
            fetchAttemptedRef.current = false;
            fetchDeepAnalysis();
          }, 5000);
        } else if (data.queued) {
          notify.info('Reanalysis queued - results will update shortly');
          // Poll for results after a delay
          setTimeout(() => {
            fetchAttemptedRef.current = false;
            fetchDeepAnalysis();
          }, 5000);
        }
      } else {
        notify.error('Failed to reanalyze email');
      }
    } catch (err) {
      console.error('Reanalysis error:', err);
      notify.error('Reanalysis failed');
    } finally {
      setIsReanalyzing(false);
    }
  };

  const submitThreatDispute = async () => {
    if (!messageId || !accountId || disputeSubmitting) return;
    if (!threadId) {
      notify.error('Thread context is required to submit a dispute');
      return;
    }
    const text = disputeComment.trim();
    if (!text) {
      notify.error('Please add a short explanation');
      return;
    }
    setDisputeSubmitting(true);
    try {
      const tokens = authService.getTokens();
      if (!tokens?.access_token) {
        notify.error('Please log in');
        return;
      }
      const response = await fetch('/api/v1/email/deep-security/dispute', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${tokens.access_token}`,
        },
        body: JSON.stringify({
          accountId,
          threadId,
          messageId,
          senderEmail,
          threatScore: deepAnalysis?.threat_score,
          threatCategory: deepAnalysis?.threat_category,
          comment: text,
        }),
      });
      let data: { success?: boolean; error?: string } = {};
      try {
        data = await response.json();
      } catch {
        /* ignore */
      }
      if (response.ok && data.success) {
        setDisputeSubmitted(true);
        notify.success('Feedback submitted for review. This does not change the automated verdict.');
      } else {
        notify.error(data.error || 'Could not submit feedback');
      }
    } catch (err) {
      console.error('Threat dispute submit error:', err);
      notify.error('Could not submit feedback');
    } finally {
      setDisputeSubmitting(false);
    }
  };
  
  // Actually perform the add-to-contacts API call
  const performAddToContacts = async () => {
    if (!senderEmail) return;
    
    setIsAddingContact(true);
    try {
      const tokens = authService.getTokens();
      if (!tokens?.access_token) {
        notify.error('Please log in to add contacts');
        return;
      }
      
      const response = await fetch('/api/v1/email-contacts', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${tokens.access_token}`
        },
        body: JSON.stringify({
          action: 'autoSaveContact',
          email: senderEmail,
          name: senderName || senderEmail.split('@')[0]
        })
      });
      
      if (response.ok) {
        notify.success('Contact added successfully');
        onAddToContacts?.(senderEmail, senderName || '');
      } else {
        notify.error('Failed to add contact');
      }
    } catch (err) {
      console.error('Failed to add contact:', err);
      notify.error('Failed to add contact');
    } finally {
      setIsAddingContact(false);
    }
  };

  // Handle add to contacts — show risk confirmation first
  const handleAddToContacts = () => {
    if (!senderEmail) return;
    
    const domain = senderEmail.split('@')[1] || 'unknown';
    
    notify.confirm({
      title: 'Add External Sender to Contacts?',
      message: `You are about to add "${senderName || senderEmail}" (${senderEmail}) to your trusted contacts.\n\nOnce added, emails from this sender will no longer show security warnings. If this sender is malicious or their account is compromised in the future, you will not be alerted.\n\nOnly add contacts you recognise and trust.`,
      confirmText: 'Yes, Add to Contacts',
      cancelText: 'Cancel',
      variant: 'warning',
      requireConfirmation: {
        type: 'checkbox',
        label: `I verify that I know and trust this sender from ${domain}`
      },
      onConfirm: () => {
        performAddToContacts();
      }
    });
  };
  
  // No analysis or no issues to show
  if (!securityAnalysis) return null;
  
  // Skip if warning level is none AND no deep analysis threats detected AND not an external sender
  // Don't skip if message-level data shows a high deep_threat_score (post-scan update)
  // Always allow external senders through so they get the external_info banner
  const hasDeepThreats = (securityAnalysis?.deep_threat_score ?? 0) >= 30 || 
    securityAnalysis?.deep_threat_category === 'spoofing' ||
    securityAnalysis?.deep_threat_category === 'phishing' ||
    securityAnalysis?.deep_threat_category === 'bec';
  const isExternalNotInContacts = securityAnalysis.is_external && !securityAnalysis.is_known_contact;
  if (securityAnalysis.warning_level === 'none' && !hasDeepThreats && !isExternalNotInContacts) return null;
  
  // Skip if sender is already verified or warnings dismissed
  if (senderIdentification?.verified || isDismissed) return null;
  
  // Get banner type - prefer deep analysis results when available
  const deepBannerType = getBannerTypeFromDeepAnalysis(deepAnalysis, securityAnalysis);
  const basicBannerType = getBannerType(securityAnalysis);
  
  // If deep analysis cleared the email, still show external sender info if applicable
  // BUT: Never consider 'cleared' if the message-level security_analysis shows high threat scores
  // This prevents stale cached deepAnalysis from hiding real threats
  const msgDeepScore = securityAnalysis?.deep_threat_score;
  const msgHasThreats = msgDeepScore !== undefined && msgDeepScore !== null && msgDeepScore >= 30;
  const clearedByDeep = !msgHasThreats && (deepBannerType === DEEP_ANALYSIS_CLEARED || clearedByDeepAnalysis);
  if (clearedByDeep) {
    // Even when cleared, show informational banner for external senders not in contacts
    if (securityAnalysis.is_external && !securityAnalysis.is_known_contact) {
      // Use external_info banner - a soft informational banner
      const config = BANNER_CONFIGS['external_info'];
      return (
        <div className={cn(
          'border rounded-lg overflow-hidden',
          config.bgClass,
          className
        )}>
          <div className="flex items-center justify-between p-3">
            <div className="flex items-center gap-3 min-w-0 flex-1">
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src="/icons/threat-analysis-icon.png"
                alt="Security"
                width={44}
                height={44}
                className="flex-shrink-0"
                style={{ margin: 0, padding: 0 }}
              />
              <div className="flex flex-col min-w-0">
                <span className={cn('font-medium text-sm truncate', config.textClass)}>
                  External Sender {!securityAnalysis.is_known_contact ? '• Not in Contacts' : ''}
                </span>
                <span className="text-xs opacity-75 truncate">
                  {senderEmail ? `From: ${senderEmail}` : 'This email is from outside your organization'}
                  {deepAnalysis?.domain_is_trusted_free_provider && deepAnalysis?.domain_free_provider && (
                    <> • {deepAnalysis.domain_free_provider}</>
                  )}
                  {deepAnalysis && <> • Security scan: Clean</>}
                </span>
              </div>
            </div>
            {/* Add to contacts button */}
            {senderEmail && !securityAnalysis.is_known_contact && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleAddToContacts();
                }}
                disabled={isAddingContact}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md bg-white/50 dark:bg-black/20 hover:bg-white/70 dark:hover:bg-black/30 transition-colors disabled:opacity-50"
              >
                <UserPlus className="w-3.5 h-3.5" />
                {isAddingContact ? 'Adding...' : 'Add to Contacts'}
              </button>
            )}
          </div>
        </div>
      );
    }
    // Fully cleared and in contacts - no banner needed
    return null;
  }
  
  // Prioritize impersonation from basic analysis - it's more specific than generic spoofing from deep analysis
  // Also derive banner type from message-level deep analysis data when the full deepAnalysis object
  // hasn't been fetched yet (e.g., right after a rescan when cache was invalidated)
  let msgDeepBannerType: BannerType | null = null;
  if (!deepBannerType && msgHasThreats) {
    const msgCat = securityAnalysis?.deep_threat_category;
    if (msgCat === 'phishing') msgDeepBannerType = 'phishing_threat';
    else if (msgCat === 'bec') msgDeepBannerType = 'bec_threat';
    else if (msgCat === 'spoofing') {
      const basic = securityAnalysis;
      const authStrong =
        basic.spf_passed === true &&
        basic.dmarc_passed === true &&
        (basic.dkim_passed === true || basic.dkim_passed === null);
      const softScore = msgDeepScore == null || msgDeepScore < 55;
      if (!shouldSuppressListSecurityIndicator(basic) && !(authStrong && softScore)) {
        msgDeepBannerType = 'spoofing_threat';
      }
    } else if (msgDeepScore >= 40) msgDeepBannerType = 'suspicious_sender';
  }
  
  const bannerType = basicBannerType === 'impersonation' 
    ? 'impersonation' 
    : (deepBannerType && deepBannerType !== DEEP_ANALYSIS_CLEARED) ? deepBannerType 
    : msgDeepBannerType || basicBannerType;
  
  // If no specific threat banner but email is external and not in contacts, show external_info
  if (!bannerType && isExternalNotInContacts) {
    const config = BANNER_CONFIGS['external_info'];
    return (
      <div className={cn(
        'border rounded-lg overflow-hidden',
        config.bgClass,
        className
      )}>
        <div className="flex items-center justify-between p-3">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src="/icons/threat-analysis-icon.png"
              alt="Security"
              width={44}
              height={44}
              className="flex-shrink-0"
              style={{ margin: 0, padding: 0 }}
            />
            <div className="flex flex-col min-w-0">
              <span className={cn('font-medium text-sm truncate', config.textClass)}>
                External Sender • Not in Contacts
              </span>
              <span className="text-xs opacity-75 truncate">
                {senderEmail ? `From: ${senderEmail}` : 'This email is from outside your organization'}
              </span>
            </div>
          </div>
          {senderEmail && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleAddToContacts();
              }}
              disabled={isAddingContact}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md bg-white/50 dark:bg-black/20 hover:bg-white/70 dark:hover:bg-black/30 transition-colors disabled:opacity-50"
            >
              <UserPlus className="w-3.5 h-3.5" />
              {isAddingContact ? 'Adding...' : 'Add to Contacts'}
            </button>
          )}
        </div>
      </div>
    );
  }
  
  if (!bannerType) return null;
  
  // Check if this banner type should be shown based on client settings
  if (settings?.banners) {
    const { banners } = settings;
    if (bannerType === 'impersonation' && !banners.show_impersonation_warning) return null;
    if ((bannerType === 'spam' || bannerType === 'spam_warning') && !banners.show_spam_warning) return null;
    if (bannerType === 'auth_failure' && !banners.show_auth_failure_warning) return null;
    if ((bannerType === 'unknown_sender' || bannerType === 'unknown_first_time') && !banners.show_unknown_sender_warning) return null;
    if (bannerType === 'first_time' && !banners.show_first_time_sender_warning) return null;
    // Deep analysis banners (Total Threat Analysis) — client can disable entirely
    if (banners.show_deep_analysis_banner === false && 
        ['phishing_threat', 'bec_threat', 'spoofing_threat', 'suspicious_sender'].includes(bannerType)) {
      return null;
    }
  }
  
  // Check whitelist - skip banner if sender is whitelisted
  if (settings?.lists && senderEmail) {
    const emailLower = senderEmail.toLowerCase();
    const domain = emailLower.split('@')[1];
    
    // Check email whitelist
    if (settings.lists.whitelisted_emails.some(e => e.toLowerCase() === emailLower)) {
      return null;
    }
    // Check domain whitelist
    if (domain && settings.lists.whitelisted_domains.some(d => d.toLowerCase() === domain)) {
      return null;
    }
  }
  
  // Get base config and apply client customizations for external sender banners
  let config = BANNER_CONFIGS[bannerType];
  
  // Apply client-custom label and severity for all external sender banners
  if (['suspicious_sender', 'unknown_first_time', 'unknown_sender'].includes(bannerType) && settings?.banners) {
    const { external_banner_label, external_banner_severity } = settings.banners;
    const customLabel = external_banner_label?.trim();
    
    // Severity-based style overrides
    const severityStyles = {
      info: {
        bgClass: 'bg-slate-50 dark:bg-slate-900/20 border-slate-200 dark:border-slate-700',
        textClass: 'text-slate-700 dark:text-slate-300',
        iconClass: 'text-slate-500 dark:text-slate-400',
      },
      warning: {
        bgClass: 'bg-orange-100 dark:bg-orange-900/30 border-orange-300 dark:border-orange-700',
        textClass: 'text-orange-900 dark:text-orange-100',
        iconClass: 'text-orange-700 dark:text-orange-300',
      },
      danger: {
        bgClass: 'bg-red-100 dark:bg-red-900/30 border-red-300 dark:border-red-700',
        textClass: 'text-red-900 dark:text-red-100',
        iconClass: 'text-red-700 dark:text-red-300',
      },
    };
    
    const severity = external_banner_severity && severityStyles[external_banner_severity] 
      ? external_banner_severity 
      : 'warning';
    
    config = {
      ...config,
      ...severityStyles[severity],
      title: `Total Threat Analysis - ${customLabel || 'Suspicious Sender'}`,
    };
  }
  
  const description = getBannerDescription(bannerType, securityAnalysis, senderEmail, deepAnalysis);

  const useDeepThreatChrome = DEEP_THREAT_BANNER_TYPES.has(bannerType);
  const showThreatDispute =
    resubmitHandled &&
    !disputeSubmitted &&
    !!deepAnalysis &&
    !!messageId &&
    !!accountId &&
    !!threadId &&
    !clearedByDeepAnalysis &&
    useDeepThreatChrome;
  
  // Build dynamic title with threat info when deep analysis is available
  const getThreatSummary = () => {
    if (!deepAnalysis) return null;
    const score = Math.round(deepAnalysis.threat_score);
    const category = deepAnalysis.threat_category;
    if (score < 20 && category === 'clean') return null;
    return { score, category };
  };
  const threatSummary = getThreatSummary();

  // Handle verify/dismiss sender via API
  const handleVerifySender = async (action: 'verify' | 'dismiss') => {
    if (!threadId) {
      // Fall back to callback if no threadId
      onVerifySender?.(action);
      if (action === 'dismiss') {
        setIsDismissed(true);
      }
      return;
    }

    setIsVerifying(true);
    try {
      const tokens = authService.getTokens();
      if (!tokens?.access_token) {
        notify.error('Please log in');
        return;
      }

      const response = await fetch('/api/v1/email/threads/verify-sender', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${tokens.access_token}`
        },
        body: JSON.stringify({
          threadId,
          accountId,
          action,
          contactName: senderName || senderEmail
        })
      });

      let data: {
        success?: boolean;
        error?: string;
        data?: {
          moved_to_folder?: string | null;
        };
        moved_to_folder?: string | null;
      } = {};

      try {
        data = await response.json();
      } catch {
        data = {};
      }

      if (response.ok) {
        const movedToFolder = data.data?.moved_to_folder ?? data.moved_to_folder ?? null;
        if (action === 'verify') {
          notify.success(
            movedToFolder
              ? `Sender verified and email moved to ${movedToFolder}`
              : 'Sender verified'
          );
        } else {
          notify.success(
            movedToFolder
              ? `Warning dismissed and email moved to ${movedToFolder}`
              : 'Warning dismissed'
          );
        }
        setIsDismissed(true);
        onSenderVerified?.(true, { movedToFolder });
        onVerifySender?.(action);
      } else {
        notify.error(data.error || 'Failed to update sender status');
      }
    } catch (err) {
      console.error('Failed to verify sender:', err);
      notify.error('Failed to verify sender');
    } finally {
      setIsVerifying(false);
    }
  };
  
  // Compact mode for mobile/inline display
  if (compact) {
    return (
      <div className={cn(
        'flex items-center gap-2 px-3 py-2 rounded-lg border text-sm',
        config.bgClass,
        className
      )}>
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src="/icons/threat-analysis-icon-sml.png"
          alt="Security"
          width={24}
          height={24}
          className="flex-shrink-0"
          style={{ margin: 0, padding: 0 }}
        />
        <span className={cn('font-medium', config.textClass)}>{config.title}</span>
        {threatSummary && (
          <span className={cn(
            'ml-auto px-2 py-0.5 rounded-full text-xs font-semibold',
            threatSummary.score >= 50 ? 'bg-red-200 dark:bg-red-800 text-red-800 dark:text-red-200' : 'bg-orange-200 dark:bg-orange-800 text-orange-800 dark:text-orange-200'
          )}>
            {threatSummary.score}/100
          </span>
        )}
      </div>
    );
  }
  
  return (
    <div
      className={cn(
        'rounded-lg overflow-hidden',
        useDeepThreatChrome
          ? cn('border-2 border-red-400 dark:border-red-500', className)
          : cn('border', config.bgClass, className)
      )}
    >
      {/* Header - always visible */}
      <div 
        className={cn(
          'flex items-center justify-between cursor-pointer',
          useDeepThreatChrome &&
            'px-2 py-1.5 sm:px-3 bg-red-100 dark:bg-red-950/50 border-b border-red-200/80 dark:border-red-900/45'
        )}
        style={{ padding: useDeepThreatChrome ? undefined : 0, margin: 0 }}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-3 min-w-0 flex-1">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src="/icons/threat-analysis-icon-sml.png"
            alt="Security"
            className="flex-shrink-0"
            style={{ margin: 0, padding: 0, width: 52, height: 52 }}
          />
          <div className="flex flex-col min-w-0">
            <div className="flex items-center gap-2">
              <span className={cn('font-medium text-sm truncate', config.textClass)}>
                {config.title}
              </span>
            </div>
            {/* Show threat info below title when deep analysis available */}
            {threatSummary && (
              <span className="text-xs opacity-75 truncate">
                Threat Score: {threatSummary.score}/100 • {threatSummary.category.charAt(0).toUpperCase() + threatSummary.category.slice(1)}
                {/* Only show domain reputation if negative AND not from trusted provider */}
                {deepAnalysis?.domain_reputation_score != null && 
                 deepAnalysis.domain_reputation_score < 0 && 
                 !deepAnalysis.domain_is_trusted_free_provider && (
                  <> • Domain reputation: {deepAnalysis.domain_reputation_score}</>
                )}
                {/* Show provider info for trusted free providers */}
                {deepAnalysis?.domain_is_trusted_free_provider && deepAnalysis?.domain_free_provider && (
                  <> • Provider: {deepAnalysis.domain_free_provider}</>
                )}
              </span>
            )}
            {/* Show loading indicator while fetching deep analysis */}
            {isLoadingDeepAnalysis && !threatSummary && (
              <span className="text-xs opacity-75 flex items-center gap-1">
                <Loader2 className="w-3 h-3 animate-spin" />
                Analyzing...
              </span>
            )}
          </div>
        </div>
        
        {/* Threat score badge — thresholds aligned with DeepAnalysisPanel */}
        {threatSummary && (
          <span className={cn(
            'px-2 py-1 rounded-lg text-xs font-bold mr-2',
            threatSummary.score >= 70 ? 'bg-red-200 dark:bg-red-800 text-red-800 dark:text-red-100' :
            threatSummary.score >= 40 ? 'bg-orange-200 dark:bg-orange-800 text-orange-800 dark:text-orange-100' :
            threatSummary.score >= 20 ? 'bg-yellow-200 dark:bg-yellow-800 text-yellow-800 dark:text-yellow-100' :
            'bg-green-200 dark:bg-green-800 text-green-800 dark:text-green-100'
          )}>
            {threatSummary.score >= 70 ? 'High Risk' : threatSummary.score >= 40 ? 'Medium Risk' : threatSummary.score >= 20 ? 'Low Risk' : 'Clean'}
          </span>
        )}
        
        <button className={cn('p-1 rounded hover:bg-black/5 dark:hover:bg-white/5 flex-shrink-0', config.textClass)}>
          {isExpanded ? (
            <ChevronUp className="w-4 h-4" />
          ) : (
            <ChevronDown className="w-4 h-4" />
          )}
        </button>
      </div>
      
      {/* Expanded content */}
      {isExpanded && (
        <div
          className={cn(
            'px-3 pb-3 pt-2',
            useDeepThreatChrome
              ? 'bg-red-50/95 text-red-950 dark:bg-zinc-950 dark:text-zinc-200 border-t border-red-100 dark:border-red-900/35'
              : config.textClass
          )}
        >
          {/* Red threat icon - floated right so text wraps around it */}
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src="/icons/red-threat-icon.png"
            alt="Threat"
            className="float-right"
            style={{ width: 120, height: 'auto', margin: '0 0 2px 10px', padding: '0 0 2px 0' }}
          />
          <p className="text-sm mb-3 opacity-90">
            {description}
          </p>
          
          {/* Additional warnings */}
          {securityAnalysis.warnings && securityAnalysis.warnings.length > 0 && (
            <ul className="text-sm mb-3 space-y-1">
              {securityAnalysis.warnings.slice(0, 4).map((warning, idx) => (
                <li key={idx} className="flex items-center gap-2">
                  <AlertTriangle className="w-3 h-3 flex-shrink-0" />
                  <span>{formatWarning(warning)}</span>
                </li>
              ))}
            </ul>
          )}
          
          {/* Auth status details */}
          {bannerType === 'auth_failure' && (
            <div className="mb-3 pt-2 border-t border-current/10">
              <p className="text-xs font-medium mb-1">Authentication Details:</p>
              <div className="flex items-center gap-4 text-xs flex-wrap">
                <span className={cn(
                  'flex items-center gap-1',
                  securityAnalysis.spf_passed ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                )}>
                  {securityAnalysis.spf_passed ? <ShieldCheck className="w-3 h-3" /> : <ShieldX className="w-3 h-3" />}
                  SPF: {securityAnalysis.spf_passed ? 'Pass' : 'Fail'}
                </span>
                <span className={cn(
                  'flex items-center gap-1',
                  securityAnalysis.dkim_passed ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                )}>
                  {securityAnalysis.dkim_passed ? <ShieldCheck className="w-3 h-3" /> : <ShieldX className="w-3 h-3" />}
                  DKIM: {securityAnalysis.dkim_passed ? 'Pass' : 'Fail'}
                </span>
                <span className={cn(
                  'flex items-center gap-1',
                  securityAnalysis.dmarc_passed ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                )}>
                  {securityAnalysis.dmarc_passed ? <ShieldCheck className="w-3 h-3" /> : <ShieldX className="w-3 h-3" />}
                  DMARC: {securityAnalysis.dmarc_passed ? 'Pass' : 'Fail'}
                </span>
              </div>
            </div>
          )}
          
          {/* Actions */}
          <div className={cn(
            'flex items-center gap-2 pt-2 flex-wrap',
            useDeepThreatChrome ? 'border-t border-red-200/50 dark:border-zinc-800' : 'border-t border-current/10'
          )}>
            {/* Add to Contacts - for unknown/first-time senders */}
            {(bannerType === 'unknown_sender' || bannerType === 'unknown_first_time' || bannerType === 'first_time') && senderEmail && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleAddToContacts();
                }}
                disabled={isAddingContact}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md bg-white/50 dark:bg-black/20 hover:bg-white/70 dark:hover:bg-black/30 transition-colors disabled:opacity-50"
              >
                <UserPlus className="w-3.5 h-3.5" />
                {isAddingContact ? 'Adding...' : 'Add to Contacts'}
              </button>
            )}
            
            {/* Report Phishing - for impersonation/spam */}
            {(bannerType === 'impersonation' || bannerType === 'spam') && onReportPhishing && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onReportPhishing();
                }}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md bg-white/50 dark:bg-black/20 hover:bg-white/70 dark:hover:bg-black/30 transition-colors"
              >
                <Flag className="w-3.5 h-3.5" />
                Report Phishing
              </button>
            )}
            
            {/* Block Sender */}
            {(bannerType === 'impersonation' || bannerType === 'spam' || bannerType === 'unknown_first_time') && onBlockSender && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onBlockSender();
                }}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md bg-white/50 dark:bg-black/20 hover:bg-white/70 dark:hover:bg-black/30 transition-colors"
              >
                <Ban className="w-3.5 h-3.5" />
                Block Sender
              </button>
            )}
            
            {/* Mark as Safe - for low/medium warnings */}
            {onMarkAsSafe && (bannerType !== 'spam' && bannerType !== 'impersonation') && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onMarkAsSafe();
                }}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md bg-white/50 dark:bg-black/20 hover:bg-white/70 dark:hover:bg-black/30 transition-colors"
              >
                <Check className="w-3.5 h-3.5" />
                Mark as Safe
              </button>
            )}

            {/* Verify Sender - confirm this is legitimate (for impersonation warnings) */}
            {bannerType === 'impersonation' && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleVerifySender('verify');
                }}
                disabled={isVerifying}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 hover:bg-green-200 dark:hover:bg-green-900/50 transition-colors disabled:opacity-50"
              >
                <ShieldCheck className="w-3.5 h-3.5" />
                {isVerifying ? 'Verifying...' : 'I Know This Sender'}
              </button>
            )}

            {/* Dismiss Warning - just hide the warning without full verification */}
            {(bannerType === 'unknown_sender' || bannerType === 'unknown_first_time' || bannerType === 'first_time' || bannerType === 'auth_failure') && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleVerifySender('dismiss');
                }}
                disabled={isVerifying}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
              >
                <X className="w-3.5 h-3.5" />
                {isVerifying ? 'Dismissing...' : 'Dismiss'}
              </button>
            )}
            
            {/* Deep Security Scan Button */}
            {messageId && accountId && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (deepAnalysis) {
                    setShowDeepAnalysisPanel(!showDeepAnalysisPanel);
                  } else {
                    runDeepScan();
                  }
                }}
                disabled={isRunningDeepScan}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 hover:bg-indigo-200 dark:hover:bg-indigo-900/50 transition-colors disabled:opacity-50"
              >
                {isRunningDeepScan ? (
                  <>
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    Scanning...
                  </>
                ) : deepAnalysis ? (
                  <>
                    <Scan className="w-3.5 h-3.5" />
                    {showDeepAnalysisPanel ? 'Hide Analysis' : 'View Deep Analysis'}
                  </>
                ) : (
                  <>
                    <Scan className="w-3.5 h-3.5" />
                    Run Deep Scan
                  </>
                )}
              </button>
            )}

            {/* Resubmit for Analysis - when deep analysis was already run */}
            {deepAnalysis && messageId && accountId && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  reanalyzeEmail();
                }}
                disabled={isReanalyzing}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 hover:bg-purple-200 dark:hover:bg-purple-900/50 transition-colors disabled:opacity-50"
              >
                {isReanalyzing ? (
                  <>
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    Reanalyzing...
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-3.5 h-3.5" />
                    Resubmit Analysis
                  </>
                )}
              </button>
            )}
          </div>

          {showThreatDispute && (
            <div
              className={cn(
                'mt-3 space-y-2 rounded-md p-3',
                'bg-white/60 dark:bg-zinc-900/80 border border-red-200/60 dark:border-zinc-700'
              )}
              onClick={(e) => e.stopPropagation()}
            >
              <p className="text-xs font-medium text-red-900 dark:text-zinc-200">
                Still believe this assessment is wrong?
              </p>
              <p className="text-[11px] opacity-80 dark:text-zinc-400">
                Describe why you trust this message. Your feedback is reviewed for model and rule improvements only —
                it does not automatically clear the warning.
              </p>
              <textarea
                value={disputeComment}
                onChange={(e) => setDisputeComment(e.target.value)}
                rows={4}
                maxLength={4000}
                placeholder="e.g. This is our bank's legitimate OTP domain; registration and SPF/DKIM align…"
                className={cn(
                  'w-full text-sm rounded-md border px-2 py-1.5 resize-y min-h-[88px]',
                  'border-red-200/80 bg-white text-gray-900',
                  'dark:border-zinc-600 dark:bg-zinc-950 dark:text-zinc-100 dark:placeholder:text-zinc-500'
                )}
              />
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  submitThreatDispute();
                }}
                disabled={disputeSubmitting}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md bg-amber-100 dark:bg-amber-950/50 text-amber-900 dark:text-amber-200 border border-amber-300/60 dark:border-amber-800/60 hover:bg-amber-200/80 dark:hover:bg-amber-900/40 transition-colors disabled:opacity-50"
              >
                {disputeSubmitting ? (
                  <>
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    Submitting…
                  </>
                ) : (
                  <>
                    <MessageSquare className="w-3.5 h-3.5" />
                    Submit dispute
                  </>
                )}
              </button>
            </div>
          )}
          
          {/* Deep Analysis Panel */}
          {showDeepAnalysisPanel && deepAnalysis && (
            <DeepAnalysisPanel analysis={deepAnalysis} securityAnalysis={securityAnalysis} />
          )}
        </div>
      )}
    </div>
  );
}

// Deep Analysis Panel Component
// Maps flat snake_case fields from emailDeepSecurityService.js to display sections
function DeepAnalysisPanel({ analysis, securityAnalysis }: { analysis: DeepSecurityAnalysis; securityAnalysis?: SecurityAnalysis }) {
  // When basic analysis detected impersonation but deep analysis scored low,
  // reconcile by using an effective score that accounts for impersonation
  const isImpersonationOverride = securityAnalysis?.is_potential_impersonation && analysis.threat_score < 30;
  const effectiveScore = isImpersonationOverride ? Math.max(analysis.threat_score, 75) : analysis.threat_score;
  
  const getThreatColor = (score: number) => {
    if (score >= 70) return 'text-red-600 dark:text-red-400';
    if (score >= 40) return 'text-orange-600 dark:text-orange-400';
    if (score >= 20) return 'text-yellow-600 dark:text-yellow-400';
    return 'text-green-600 dark:text-green-400';
  };
  
  const getThreatBg = (score: number) => {
    if (score >= 70) return 'bg-red-100 dark:bg-red-900/30';
    if (score >= 40) return 'bg-orange-100 dark:bg-orange-900/30';
    if (score >= 20) return 'bg-yellow-100 dark:bg-yellow-900/30';
    return 'bg-green-100 dark:bg-green-900/30';
  };
  
  const threatLevel = effectiveScore >= 70 ? 'High Risk' : 
                     effectiveScore >= 40 ? 'Medium Risk' : 
                     effectiveScore >= 20 ? 'Low Risk' : 'Clean';

  // Derive display values from flat snake_case backend fields
  const isBlacklisted = Array.isArray(analysis.ip_blacklist_hits) && analysis.ip_blacklist_hits.length > 0;
  const blacklistSources = isBlacklisted ? analysis.ip_blacklist_hits!.map(h => typeof h === 'string' ? h : h.source) : [];
  const hasBEC = (analysis.content_urgency_score && analysis.content_urgency_score > 0) ||
                 (analysis.content_financial_indicators && analysis.content_financial_indicators.length > 0);
  const suspiciousUrls = analysis.content_suspicious_urls || [];
  // Parse sender_history — may be a JSON string from DB or already an object
  const senderHistory = typeof analysis.sender_history === 'string' 
    ? (() => { try { return JSON.parse(analysis.sender_history as string); } catch { return null; } })()
    : analysis.sender_history;
  // Parse threat_indicators — may be a JSON string from DB or already an array
  const threatIndicators = typeof analysis.threat_indicators === 'string'
    ? (() => { try { return JSON.parse(analysis.threat_indicators as unknown as string); } catch { return []; } })()
    : analysis.threat_indicators;

  const domainHistoryRaw = typeof analysis.domain_history === 'string'
    ? (() => { try { return JSON.parse(analysis.domain_history as string); } catch { return null; } })()
    : analysis.domain_history;
  const domainHistory = domainHistoryRaw && typeof domainHistoryRaw === 'object' ? domainHistoryRaw as { registration_date?: string; source?: string } : null;
  const rdapRegistration = domainHistory?.registration_date ? new Date(domainHistory.registration_date) : null;
  const rdapLabel = rdapRegistration && !Number.isNaN(rdapRegistration.getTime())
    ? rdapRegistration.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
    : null;
  const effectiveDomainAgeDays =
    analysis.domain_age_days ??
    (rdapRegistration && !Number.isNaN(rdapRegistration.getTime())
      ? Math.floor((Date.now() - rdapRegistration.getTime()) / 86400000)
      : null);
  
  return (
    <div className="mt-3 pt-3 border-t border-current/10 space-y-3">
      {/* Threat Score Header */}
      <div className={cn('flex items-center justify-between p-2 rounded-lg', getThreatBg(effectiveScore))}>
        <div className="flex items-center gap-2">
          <TrendingUp className={cn('w-5 h-5', getThreatColor(effectiveScore))} />
          <span className="text-sm font-medium">Deep Analysis Complete</span>
        </div>
        <div className="text-right">
          <div className={cn('text-lg font-bold', getThreatColor(effectiveScore))}>
            {Math.round(effectiveScore)}/100
          </div>
          <div className="text-xs opacity-75">{threatLevel}</div>
        </div>
      </div>
      
      {/* Impersonation override notice */}
      {isImpersonationOverride && (
        <div className="flex items-start gap-2 p-2 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
          <AlertTriangle className="w-4 h-4 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
          <div className="text-xs text-red-700 dark:text-red-300">
            <span className="font-medium">Impersonation detected:</span> Real-time analysis identified this sender as impersonating a known contact. 
            The deep scan score has been adjusted to reflect this critical threat. 
            {securityAnalysis?.impersonation_details && (
              <span className="block mt-1 opacity-80">
                Display name matches &ldquo;{securityAnalysis.impersonation_details?.real_contact?.name || 'a known contact'}&rdquo; but the email address does not match.
              </span>
            )}
          </div>
        </div>
      )}
      
      {/* Threat Indicators */}
      {threatIndicators && threatIndicators.length > 0 && (
        <div className="space-y-1">
          <div className="flex items-center gap-1 text-xs font-medium opacity-75">
            <FileWarning className="w-3 h-3" />
            Threat Indicators
          </div>
          <div className="flex flex-wrap gap-1">
            {threatIndicators.map((indicator: { indicator: string; severity: string; explanation: string } | string, idx: number) => (
              <span key={idx} className="px-2 py-0.5 text-xs rounded-full bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300">
                {typeof indicator === 'string' ? indicator : indicator.indicator}
              </span>
            ))}
          </div>
          {/* Reconciliation note: if "No email authentication" indicator exists but domain actually has SPF/DMARC */}
          {(() => {
            const hasNoAuthIndicator = threatIndicators.some((ind: { indicator: string } | string) => {
              const name = typeof ind === 'string' ? ind : ind.indicator;
              return name.toLowerCase().includes('no email authentication') || name.toLowerCase().includes('no auth');
            });
            const domainHasAuth = analysis.domain_has_valid_spf || analysis.domain_has_dmarc;
            const headerAuthPassed = securityAnalysis?.spf_passed === true || securityAnalysis?.dkim_passed === true || securityAnalysis?.dmarc_passed === true;
            if (hasNoAuthIndicator && (domainHasAuth || headerAuthPassed)) {
              return (
                <div className="mt-1 px-2 py-1 rounded bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 text-[10px]">
                  ℹ️ Note: The sender&apos;s domain does have {[
                    analysis.domain_has_valid_spf && 'SPF',
                    analysis.domain_has_dmarc && 'DMARC',
                    headerAuthPassed && 'verified authentication'
                  ].filter(Boolean).join(', ')} records. The &quot;No email authentication&quot; indicator may reflect header parsing, not actual DNS records.
                </div>
              );
            }
            return null;
          })()}
        </div>
      )}
      
      {/* IP Reputation — from flat fields: sender_ip, ip_reputation_score, ip_country_code, ip_blacklist_hits */}
      {analysis.sender_ip && (
        <div className="space-y-1">
          <div className="flex items-center gap-1 text-xs font-medium opacity-75">
            <Server className="w-3 h-3" />
            IP Reputation ({analysis.sender_ip})
          </div>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div className={cn(
              'px-2 py-1 rounded',
              isBlacklisted ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300' : 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
            )}>
              {isBlacklisted ? '⚠️ Blacklisted' : '✓ Not Blacklisted'}
            </div>
            {analysis.ip_country_code && (
              <div className="px-2 py-1 rounded bg-gray-100 dark:bg-gray-800">
                📍 {analysis.ip_country_code}
              </div>
            )}
            {analysis.ip_reputation_score !== undefined && analysis.ip_reputation_score !== null && (
              <div className={cn(
                'px-2 py-1 rounded',
                analysis.ip_reputation_score < -20 ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300' : 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
              )}>
                IP Score: {analysis.ip_reputation_score}
              </div>
            )}
            {analysis.ip_is_datacenter && (
              <div className="px-2 py-1 rounded bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300">
                🖥️ Datacenter IP
              </div>
            )}
            {blacklistSources.length > 0 && (
              <div className="px-2 py-1 rounded bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 col-span-2">
                DNSBL Hits: {blacklistSources.join(', ')}
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Domain Reputation — from flat fields: sender_domain, domain_reputation_score, domain_has_valid_mx, etc. */}
      {analysis.sender_domain && (
        <div className="space-y-1">
          <div className="flex items-center gap-1 text-xs font-medium opacity-75">
            <Globe className="w-3 h-3" />
            Domain Reputation ({analysis.sender_domain})
          </div>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div className={cn(
                'px-2 py-1 rounded',
                effectiveDomainAgeDays == null ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300' :
                effectiveDomainAgeDays < 30 ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300' : 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
              )}>
                Age: {effectiveDomainAgeDays == null 
                  ? '⚠️ Unknown'
                  : effectiveDomainAgeDays < 30 
                    ? `⚠️ ${effectiveDomainAgeDays} days (new!)` 
                    : `${effectiveDomainAgeDays.toLocaleString()} days${rdapLabel ? ` • reg. ${rdapLabel}` : ''}`}
              </div>
            <div className={cn(
              'px-2 py-1 rounded',
              analysis.domain_has_valid_mx == null ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300' :
              analysis.domain_has_valid_mx ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300' : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'
            )}>
              MX Records: {analysis.domain_has_valid_mx == null ? '?' : analysis.domain_has_valid_mx ? '✓' : '✗'}
            </div>
            <div className={cn(
              'px-2 py-1 rounded',
              analysis.domain_has_valid_spf == null ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300' :
              analysis.domain_has_valid_spf ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300' : 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300'
            )}>
              SPF Record: {analysis.domain_has_valid_spf == null ? '?' : analysis.domain_has_valid_spf ? '✓' : '✗'}
            </div>
            <div className={cn(
              'px-2 py-1 rounded',
              analysis.domain_has_dmarc == null ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300' :
              analysis.domain_has_dmarc ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300' : 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300'
            )}>
              DMARC Record: {analysis.domain_has_dmarc == null ? '?' : analysis.domain_has_dmarc ? '✓' : '✗'}
            </div>
            {/* Clarification when impersonation is detected */}
            {isImpersonationOverride && (analysis.domain_has_valid_spf || analysis.domain_has_dmarc || analysis.domain_has_valid_mx) && (
              <div className="col-span-2 px-2 py-1 rounded bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 text-[10px]">
                ⚠️ Domain DNS records exist, but this email may be spoofed — sender is impersonating a known contact
              </div>
            )}
            {analysis.domain_is_disposable && (
              <div className="px-2 py-1 rounded bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 col-span-2">
                ⚠️ Disposable Email Domain
              </div>
            )}
            {analysis.domain_is_free_email && (
              <div className="px-2 py-1 rounded bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 col-span-2">
                Free Email Provider{analysis.domain_is_trusted_free_provider ? ' (Trusted)' : ''}
              </div>
            )}
            {analysis.domain_similar_to_known && (
              <div className="px-2 py-1 rounded bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 col-span-2">
                ⚠️ Similar to: {typeof analysis.domain_similar_to_known === 'string' 
                  ? analysis.domain_similar_to_known 
                  : analysis.domain_similar_to_known.matched} ({Math.round((typeof analysis.domain_similar_to_known === 'string' ? 0 : analysis.domain_similar_to_known.similarity) * 100)}% similar)
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Header Anomalies */}
      {(analysis.sender_reply_to_mismatch || analysis.sender_return_path_mismatch || (analysis.header_anomalies && analysis.header_anomalies.length > 0)) && (
        <div className="space-y-1">
          <div className="flex items-center gap-1 text-xs font-medium opacity-75">
            <FileWarning className="w-3 h-3" />
            Header Anomalies
          </div>
          <div className="space-y-1">
            {analysis.sender_reply_to_mismatch && (
              <div className="px-2 py-1 rounded bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 text-xs">
                ⚠️ From/Reply-To domain mismatch
              </div>
            )}
            {analysis.sender_return_path_mismatch && (
              <div className="px-2 py-1 rounded bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 text-xs">
                ⚠️ From/Return-Path domain mismatch
              </div>
            )}
            {analysis.header_anomalies && (Array.isArray(analysis.header_anomalies) ? analysis.header_anomalies : []).map((anomaly, idx) => (
              <div key={idx} className={cn(
                'px-2 py-1 rounded text-xs',
                anomaly.severity === 'high' ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300' : 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300'
              )}>
                {anomaly.description}
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* BEC Indicators — from flat fields: content_urgency_score, content_financial_indicators */}
      {hasBEC && (
        <div className="space-y-1">
          <div className="flex items-center gap-1 text-xs font-medium opacity-75">
            <AlertTriangle className="w-3 h-3" />
            Business Email Compromise Detection
          </div>
          <div className="space-y-1">
            {analysis.content_urgency_score && analysis.content_urgency_score > 30 && (
              <div className="px-2 py-1 rounded bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 text-xs">
                ⏰ High Urgency Language ({analysis.content_urgency_score}%)
              </div>
            )}
            {analysis.content_financial_indicators && analysis.content_financial_indicators.length > 0 && (
              <div className="px-2 py-1 rounded bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 text-xs">
                💰 Financial Request Patterns Detected
              </div>
            )}
            {analysis.content_financial_indicators && analysis.content_financial_indicators.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-1">
                {analysis.content_financial_indicators.slice(0, 5).map((indicator, idx) => (
                  <span key={idx} className="px-1.5 py-0.5 text-xs rounded bg-gray-100 dark:bg-gray-800">
                    {indicator}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Link Analysis — from flat fields: content_suspicious_urls, content_link_mismatch_detected */}
      {suspiciousUrls.length > 0 && (
        <div className="space-y-1">
          <div className="flex items-center gap-1 text-xs font-medium opacity-75">
            <Link className="w-3 h-3" />
            Link Analysis ({suspiciousUrls.length} suspicious link{suspiciousUrls.length !== 1 ? 's' : ''})
          </div>
          <div className="space-y-1">
            {analysis.content_link_mismatch_detected && (
              <div className="px-2 py-1 rounded bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 text-xs">
                ⚠️ Link text/URL mismatch detected
              </div>
            )}
            {suspiciousUrls.slice(0, 3).map((urlInfo, idx) => (
              <div key={idx} className={cn(
                'px-2 py-1 rounded text-xs',
                urlInfo.risk === 'high' || urlInfo.risk === 'critical' 
                  ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300' 
                  : 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300'
              )}>
                🔗 {urlInfo.url?.substring(0, 60)}{(urlInfo.url?.length || 0) > 60 ? '...' : ''} 
                {urlInfo.reason && ` — ${urlInfo.reason}`}
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Display Name Analysis */}
      {analysis.sender_display_name_analysis && (
        (typeof analysis.sender_display_name_analysis === 'object') && 
        (analysis.sender_display_name_analysis.has_unicode_tricks || 
         analysis.sender_display_name_analysis.has_email_in_name || 
         analysis.sender_display_name_analysis.lookalike_detected)
      ) && (
        <div className="space-y-1">
          <div className="flex items-center gap-1 text-xs font-medium opacity-75">
            <AlertTriangle className="w-3 h-3" />
            Display Name Spoofing
          </div>
          <div className="space-y-1">
            {analysis.sender_display_name_analysis!.has_unicode_tricks && (
              <div className="px-2 py-1 rounded bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 text-xs">
                ⚠️ Unicode lookalike characters detected
              </div>
            )}
            {analysis.sender_display_name_analysis!.has_email_in_name && (
              <div className="px-2 py-1 rounded bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 text-xs">
                ⚠️ Email address embedded in display name
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Sender History — from flat field: sender_history (object with total_emails, first_seen, last_seen) */}
      {senderHistory && (
        <div className="space-y-1">
          <div className="flex items-center gap-1 text-xs font-medium opacity-75">
            <Mail className="w-3 h-3" />
            Sender History
          </div>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div className="px-2 py-1 rounded bg-gray-100 dark:bg-gray-800">
              📧 {senderHistory.total_emails || 0} emails received
            </div>
            <div className="px-2 py-1 rounded bg-gray-100 dark:bg-gray-800">
              📅 First seen: {senderHistory.first_seen ? new Date(senderHistory.first_seen).toLocaleDateString() : 'Never'}
            </div>
            {senderHistory.threads_count > 0 && (
              <div className="px-2 py-1 rounded bg-gray-100 dark:bg-gray-800 col-span-2">
                💬 {senderHistory.threads_count} email{senderHistory.threads_count !== 1 ? 's' : ''}
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* AI Assessment (Gemini) */}
      {analysis.ai_analyzed && analysis.ai_threat_assessment && (
        <div className="space-y-1">
          <div className="flex items-center gap-1 text-xs font-medium opacity-75">
            <Sparkles className="w-3 h-3" />
            AI Assessment
          </div>
          <div className="p-2 rounded bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800">
            <p className="text-xs text-indigo-800 dark:text-indigo-200 leading-relaxed">
              {analysis.ai_threat_assessment}
            </p>
            {analysis.ai_confidence != null && (
              <div className="mt-1 text-xs opacity-60">
                AI confidence: {Math.round(analysis.ai_confidence)}%
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Analysis Timestamp */}
      <div className="text-xs opacity-50 text-right">
        Analyzed: {analysis.analyzed_at ? new Date(analysis.analyzed_at).toLocaleString() : 'Unknown'}
        {analysis.analysis_duration_ms ? ` (${analysis.analysis_duration_ms}ms)` : ''}
        {analysis.ai_analyzed && ' • AI-enhanced'}
      </div>
    </div>
  );
}

// Compact inline indicator for thread list
export function SecurityIndicator({ securityAnalysis, className }: { 
  securityAnalysis: SecurityAnalysis | null; 
  className?: string;
}) {
  if (!securityAnalysis) return null;
  if (securityAnalysis.warning_level === 'none') return null;
  if (shouldSuppressListSecurityIndicator(securityAnalysis)) return null;
  
  const bannerType = getBannerType(securityAnalysis);
  if (!bannerType) return null;
  
  const config = BANNER_CONFIGS[bannerType];
  
  return (
    <div 
      className={cn('inline-flex', className)} 
      title={config.title}
    >
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        src="/icons/threat-analysis-icon-sml.png"
        alt="Security"
        width={20}
        height={20}
        className="flex-shrink-0"
        style={{ margin: 0, padding: 0 }}
      />
    </div>
  );
}
